import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';

function errMsg(e: unknown, fallback: string): string {
  return (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? fallback;
}

// ---- Empresa (perfil del tenant) ----
export interface CompanyProfile {
  id: string;
  ruc: string;
  businessName: string;
  tradeName: string | null;
  address: string | null;
  ubigeoCode: string | null;
  logoUrl: string | null;
  timezone: string;
  currency: string;
}

export function useCompanyProfile() {
  return useQuery({
    queryKey: ['company-profile'],
    queryFn: async () => (await apiClient.get<CompanyProfile>('/tenant')).data,
    retry: false,
  });
}

export function useUpdateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { businessName?: string; tradeName?: string; address?: string; ubigeoCode?: string; timezone?: string }) =>
      apiClient.put('/tenant', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['company-profile'] });
      toast.success('Datos de la empresa actualizados');
    },
    onError: (e: unknown) => toast.error(errMsg(e, 'No se pudo actualizar')),
  });
}

/** Logo que se imprime en los comprobantes. `null` lo quita. */
export function useUpdateCompanyLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (logoUrl: string | null) => apiClient.put('/tenant', { logoUrl }),
    onSuccess: (_res, logoUrl) => {
      qc.invalidateQueries({ queryKey: ['company-profile'] });
      toast.success(logoUrl ? 'Logo actualizado' : 'Logo eliminado');
    },
    onError: (e: unknown) => toast.error(errMsg(e, 'No se pudo guardar el logo')),
  });
}

// ---- Usuarios (miembros) ----
export interface Member {
  userId: string;
  email: string;
  name: string;
  role: string;
  permissions: string[];
}

export function useMembers() {
  return useQuery({
    queryKey: ['members'],
    queryFn: async () => (await apiClient.get<Member[]>('/users')).data,
    retry: false,
  });
}

export function useAddMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      email: string;
      name: string;
      password: string;
      role: string;
      permissions: string[];
    }) => apiClient.post('/users', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['members'] });
      toast.success('Usuario agregado');
    },
    onError: (e: unknown) => toast.error(errMsg(e, 'No se pudo agregar el usuario')),
  });
}

export function useUpdateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, ...data }: { userId: string; role?: string; permissions?: string[] }) =>
      apiClient.put(`/users/${userId}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['members'] }),
    onError: (e: unknown) => toast.error(errMsg(e, 'No se pudo actualizar')),
  });
}

export function useRemoveMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => apiClient.delete(`/users/${userId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['members'] });
      toast.success('Usuario removido');
    },
    onError: (e: unknown) => toast.error(errMsg(e, 'No se pudo remover')),
  });
}

// ---- Cuenta (cambio de contraseña) ----
export function useChangePassword() {
  return useMutation({
    mutationFn: async (input: { currentPassword: string; newPassword: string }) =>
      apiClient.post('/auth/change-password', input),
    onSuccess: () => toast.success('Contraseña actualizada'),
    onError: (e: unknown) => toast.error(errMsg(e, 'No se pudo cambiar la contraseña')),
  });
}

export const ASSIGNABLE_PERMISSIONS = [
  { key: 'documents.create', label: 'Emitir comprobantes' },
  { key: 'documents.send', label: 'Enviar a SUNAT' },
  { key: 'documents.void', label: 'Anular comprobantes' },
  { key: 'documents.note', label: 'Notas de credito/debito' },
  { key: 'products.write', label: 'Gestionar productos' },
  { key: 'series.manage', label: 'Gestionar series' },
  { key: 'certificates.manage', label: 'Gestionar certificado' },
];
